import { SearchContentType, SearchResultData } from '@novaheal/types';
import { useInfiniteQuery } from '@tanstack/react-query';
import algoliasearch, { SearchClient, SearchIndex } from 'algoliasearch/lite';

// Get config values from environment or config file
const ALGOLIA_APP_ID = process.env.REACT_APP_ALGOLIA_APPLICATION_ID || '';
const ALGOLIA_API_KEY = process.env.REACT_APP_ALGOLIA_SEARCH_API_KEY || '';
const ALGOLIA_INDEX_NAME = process.env.REACT_APP_ALGOLIA_INDEX_NAME || '';

const client: SearchClient = algoliasearch(ALGOLIA_APP_ID, ALGOLIA_API_KEY);
const index: SearchIndex = client.initIndex(ALGOLIA_INDEX_NAME);

const ALGOLIA_BATCH_SIZE = 3;

export type AlgoliaPageResult = {
  hits: SearchResultData[];
  nbHits: number;
  page: number;
  nbPages: number;
  typeCounts?: Record<string, number>;
};

const fetchAlgoliaContentPage = async ({
  pageParam = 0,
  query = '',
  contentType,
  batchSize = ALGOLIA_BATCH_SIZE,
  facetFilters,
}: {
  pageParam: number;
  query: string;
  contentType: SearchContentType[];
  batchSize?: number;
  facetFilters?: string[][];
}): Promise<AlgoliaPageResult> => {
  try {
    // Handle "All" content type with multiple queries
    if (contentType.length === 0) {
      const contentTypes = [
        SearchContentType.ARTICLE,
        SearchContentType.MEDICATION,
        SearchContentType.IMAGE,
        SearchContentType.VIDEO,
      ];

      const searchRequests = contentTypes.map((type) => ({
        indexName: ALGOLIA_INDEX_NAME,
        query,
        params: {
          hitsPerPage: batchSize,
          page: Math.floor(pageParam),
          filters: `contentType:"${type}"`,
        },
      }));

      const results = await client.search(searchRequests);

      // Combine all results
      const allHits: SearchResultData[] = [];
      const typeCounts: Record<string, number> = {
        articles: 0,
        medications: 0,
        media: 0,
      };

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      results.results.forEach((result: any, index: number) => {
        const contentTypeKey = contentTypes[index];
        allHits.push(...result.hits);

        // Map content types to categories for typeCounts
        if (contentTypeKey === SearchContentType.ARTICLE) {
          typeCounts.articles = result.nbHits;
        } else if (contentTypeKey === SearchContentType.MEDICATION) {
          typeCounts.medications = result.nbHits;
        } else if (
          contentTypeKey === SearchContentType.IMAGE ||
          contentTypeKey === SearchContentType.VIDEO
        ) {
          typeCounts.media += result.nbHits;
        }
      });

      const totalNbHits = Object.values(typeCounts).reduce((sum, count) => sum + count, 0);

      return {
        hits: allHits,
        nbHits: totalNbHits,
        page: Math.floor(pageParam),
        nbPages: Math.ceil(totalNbHits / batchSize),
        typeCounts,
      };
    }

    // Specific content-type selection (not "all")
    const pageIndex = Math.floor(pageParam);
    const params = {
      hitsPerPage: batchSize,
      page: pageIndex,
      filters: `contentType:"${contentType.join('" OR contentType:"')}"`,
    };

    const hasTagFacetFilters = Boolean(facetFilters && facetFilters.length > 0);

    const results = await index.search<SearchResultData>(query, {
      ...params,
      ...(hasTagFacetFilters ? { facetFilters } : {}),
    });

    return {
      hits: results.hits,
      nbHits: results.nbHits,
      page: results.page,
      nbPages: results.nbPages,
    };
  } catch (error) {
    // Log error to console for debugging in development
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.error('Algolia search error:', error);
    }
    return { hits: [], nbHits: 0, page: 0, nbPages: 0 };
  }
};

const getNextAlgoliaPageParam = (lastPage: AlgoliaPageResult): number | undefined => {
  if (!lastPage || lastPage.page >= lastPage.nbPages - 1) {
    return undefined;
  }
  return lastPage.page + 1;
};

type UseInfiniteAlgoliaContentParams = {
  contentType: SearchContentType[];
  query: string;
  disabled?: boolean;
  batchSize?: number;
  selectedCeTags?: string[];
  selectedKbTags?: string[];
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const useInfiniteAlgoliaContent = ({
  contentType,
  query,
  disabled = false,
  batchSize = ALGOLIA_BATCH_SIZE,
  selectedCeTags,
  selectedKbTags,
}: UseInfiniteAlgoliaContentParams) => {
  const queryResult = useInfiniteQuery({
    queryKey: [
      `algolia-content-${contentType.join(',')}`,
      query,
      batchSize,
      selectedCeTags?.join(',') ?? '',
      selectedKbTags?.join(',') ?? '',
    ],
    queryFn: (context) => {
      // Build facetFilters for Algolia
      const facetFilters: string[][] = [];
      if (selectedCeTags && selectedCeTags.length > 0) {
        facetFilters.push(selectedCeTags.map((tag) => `fields.tags.ce:${tag}`));
      }
      if (selectedKbTags && selectedKbTags.length > 0) {
        facetFilters.push(selectedKbTags.map((tag) => `fields.tags.kb:${tag}`));
      }
      return fetchAlgoliaContentPage({
        pageParam: context.pageParam as number,
        query,
        contentType,
        batchSize,
        facetFilters,
      });
    },
    getNextPageParam: getNextAlgoliaPageParam,
    enabled: !!query && !!contentType && !disabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    initialPageParam: 0,
  });

  // Extract typeCounts from the first page if available
  const typeCounts = queryResult.data?.pages?.[0]?.typeCounts || {};

  return {
    ...queryResult,
    typeCounts,
  };
};
